import { Divider, Stack, Typography } from '@mui/material';
import type { Account } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { PanelFooter } from '@/components/panels/panelFooter';
import { TagsSection } from '@/components/tags/tagsSection';
import { useDAL } from '@/contexts/useDAL';

interface RestoreAccountConfigCustomTagsProps {
  account: Account;
  onClose: () => void;
}

export const RestoreAccountConfigCustomTags = (
  props: RestoreAccountConfigCustomTagsProps
) => {
  const dal = useDAL();
  const { body, isLoading } = dal.cloudAccounts.restore.configuration.tags.get(
    props.account.id
  );

  const [originalTags, setOriginalTags] = useState<Record<string, string>>({});
  const [updatedTags, setUpdatedTags] = useState<Record<string, string>>({});

  useEffect(() => {
    if (body) {
      const parsed = body.reduce<Record<string, string>>((acc, tag) => {
        acc[tag.key] = tag.value || '';
        return acc;
      }, {});
      setOriginalTags(parsed);
      setUpdatedTags(parsed);
    }
  }, [body]);

  const saveTags = () => {
    void dal.cloudAccounts.restore.configuration.tags
      .set(props.account.id, updatedTags)
      .then(() => {
        props.onClose();
      });
  };

  const tagsChanged =
    JSON.stringify(originalTags) !== JSON.stringify(updatedTags);

  return (
    <Stack className='h-full'>
      <Stack className='ml-[90px] mt-[40px] overflow-y-auto' flexGrow={1}>
        <Typography variant='subtitle1' className='py-[8px]'>
          {`Restore operations are run on a temporary server. To help identify it, we’ll add this tag:`}
          <span className='border border-solid rounded-[4px] border-[#CFD9DC] ml-[8px] px-[8px] py-[4px]'>
            eon:service=true
          </span>
        </Typography>
        <Divider className='my-[24px]' />
        <TagsList
          loading={isLoading}
          tags={updatedTags}
          onChange={setUpdatedTags}
        />
      </Stack>
      <PanelFooter
        primaryButton={{
          text: 'Save changes',
          onClick: saveTags,
          disabled: !tagsChanged,
          'data-testid': 'restore-config-tags-save-btn',
        }}
        secondaryButton={{
          text: 'Cancel',
          onClick: props.onClose,
          'data-testid': 'restore-config-tags-cancel-btn',
        }}
      />
    </Stack>
  );
};

const TagsList = (props: {
  loading: boolean;
  tags: Record<string, string>;
  onChange: (tags: Record<string, string>) => void;
}) => {
  return (
    <div className='mt-[-40px] ml-[-40px] max-w-[920px]'>
      {props.loading && <Loader sx={{ mt: 50 }} />}
      {!props.loading && (
        <TagsSection
          tags={props.tags}
          initialTags={props.tags}
          keepOriginalTags={false}
          onChange={(updated) => {
            props.onChange(updated);
          }}
        />
      )}
    </div>
  );
};
